import styled from 'styled-components';
import { Button } from '../../../components/Button';
import { useGetValidationICCIDMutation, useGetValidationCodeMutation } from '../api/activateApi';
import { useEffect, useState } from 'react'; // Add this import at the top of the file
import { useFindCodeLink } from '../../subscription/hooks/useFindCodeLink';
import { useModal } from '../../../hooks/useModal';

// Add retrievedIccid to store the iccid value from the API response
type EnterCodeProps = {
  codeType: string;
  code: string;
  onCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isActive: boolean;
  onClick: (esimBatchLabel?: string) => void;
  onGoBack: () => void;
  setIccid: (iccid: string) => void;
  setReservationId: (reservationId: string) => void;
  iccid: string;
  onCodeTypeSwitch: () => void;
};

export const EnterCode = ({
  codeType,
  code,
  onCodeChange,
  isActive,
  onClick,
  setIccid,
  setReservationId,
  iccid,
  onCodeTypeSwitch,
  onGoBack
}: EnterCodeProps) => {
  const [validateICCID, { isLoading: loadingICCID }] = useGetValidationICCIDMutation();
  const [validateCode, { isLoading: loadingCode }] = useGetValidationCodeMutation();
  const isLoading = loadingCode || loadingICCID;
  const isICCID = codeType === '20-digits';
  const iccidLength = iccid?.length;
  const isValid = isICCID ? iccidLength > 18 && iccidLength < 21 : code?.length === 4;
  const [isValidate, setIsValidate] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [retrievedIccid, setRetrievedIccid] = useState<string>('');
  const [isConfirmChecked, setIsConfirmChecked] = useState(false); // Inside the component, add this state
  const [esimManualUpdate, setEsimManualUpdate] = useState<string>('');

  const { LinkButton, HelperModalComponent } = useFindCodeLink();

  // Update the modal hook - remove the width parameter
  const { ModalComponent: ConfirmationModal } = useModal({
    isOpen: showConfirmation,
    onClose: () => setShowConfirmation(false),
    title: 'SIM Validated'
  });

  const onValidate = async () => {
    try {
      if (isICCID) {
        const result = await validateICCID({ iccid }).unwrap();
        setEsimManualUpdate(result.esim_manual_update || '');
        setReservationId(result.reservation_id || '');
        setIsValidate(true);
      } else {
        const { iccid: retrievedIccidValue, esim_manual_update, reservation_id } =
          await validateCode({ code }).unwrap();
        setRetrievedIccid(retrievedIccidValue);
        setIccid(retrievedIccidValue);
        setEsimManualUpdate(esim_manual_update || '');
        setReservationId(reservation_id || '');
        setShowConfirmation(true);
      }
    } catch (error) {
      setIsValidate(false);
      // @ts-ignore
      const errorMsg = error?.data.message as string;
      if (errorMsg) {
        setErrorMessage(errorMsg);
      }
    }
  };

  const handleConfirm = () => {
    setShowConfirmation(false);
    setIsValidate(true);
  };

  useEffect(() => {
    if (isValidate) {
      onClick(esimManualUpdate || undefined);
    }
  }, [isValidate]);

  return (
    <Container width={isICCID ? '35rem' : '25rem'}>
      <Title>Enter Code</Title>
      <InputWrapper>
        <StyledInput
          isActive={isActive}
          placeholder={isICCID ? 'Enter ICCID number' : 'Enter 4-digit activation code'}
          width={'100%'}
          value={isICCID ? iccid : code}
          onChange={onCodeChange}
        />
      </InputWrapper>

      {errorMessage && (
        <>
          <Message>{errorMessage}</Message>
          <Button
            onClick={() => {
              setErrorMessage(null);
              onCodeTypeSwitch();
            }}>
            Try 20-digit method
          </Button>
        </>
      )}
      {LinkButton}

      <ButtonWrapper>
        <Button onClick={onGoBack}>Back</Button>
        <Button
          onClick={onValidate}
          disabled={!isActive || !isValid || isLoading}
          isLoading={isLoading}>
          Next
        </Button>
      </ButtonWrapper>
      {HelperModalComponent}

      {/* Confirmation Modal using custom hook */}
      <ConfirmationModal>
        <ModalContent>
          <IccidDisplay>
            <strong>Confirm the last 4 digits of your SIM&nbsp;&nbsp;(ICCID):</strong>
            <DigitContainer>
              {retrievedIccid.slice(-4)}
            </DigitContainer>
          </IccidDisplay>
          
          <p>
            Please <strong>double‑check</strong> that these four digits exactly match those printed on your camera or SIM card.
            <br/>
            <em>If they do not match, activation will <strong>fail</strong>.</em>
          </p>
          
          <CheckboxContainer>
            <input
              type="checkbox"
              id="confirm-check"
              checked={isConfirmChecked}
              onChange={(e) => setIsConfirmChecked(e.target.checked)}
            />
            <label htmlFor="confirm-check">
              I have verified these final 4 digits match my SIM
            </label>
          </CheckboxContainer>
          
          <ModalButtonWrapper>
            <Button onClick={() => setShowConfirmation(false)} variant="secondary">
              Go back and verify
            </Button>
            <Button 
              onClick={handleConfirm} 
              disabled={!isConfirmChecked}>
              Confirm &amp; proceed
            </Button>
          </ModalButtonWrapper>
        </ModalContent>
      </ConfirmationModal>
    </Container>
  );
};

const InputWrapper = styled.div`
  position: relative;
`;

export const Message = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem;
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.colors.red};
  color: ${({ theme }) => theme.colors.white};
  opacity: 0.85;
  font-size: ${({ theme }) => theme.fontSize.small};
  text-align: center;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
`;

const Title = styled.h3`
  margin: 0;
  text-align: center;
`;

const Container = styled.div<{ width: string }>`
  width: ${({ width }) => width};
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 850px) {
    width: 100%;
  }
`;

const StyledInput = styled.input<{ isActive: boolean }>`
  padding: 0.5rem;
  border-radius: 0.3rem;
  border: 1px solid ${({ theme }) => theme.colors.gray500};
  font-size: ${({ theme, isActive }) => theme.fontSize[isActive ? 'large' : 'medium']};
  text-align: center;
  font-weight: ${({ isActive }) => (isActive ? '800' : '400')};
  letter-spacing: ${({ isActive }) => (isActive ? '0.6rem' : '0')};
  width: ${({ width }) => width};
  height: 4rem;

  @media (max-width: 850px) {
    width: 100%;
    font-size: ${({ theme, isActive }) => theme.fontSize[isActive ? 'medium' : 'small']};
    height: 3rem;
    letter-spacing: ${({ isActive }) => (isActive ? '0.3rem' : '0')};
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem 1.5rem 2.5rem; /* Keep padding */
  text-align: center;
  width: 100%;
  min-width: 320px; /* Keep minimum width */
  
  @media (min-width: 500px) {
    min-width: 400px; /* Keep wider on larger screens */
  }

  p {
    margin: 0;
    font-size: ${({ theme }) => theme.fontSize.small};
  }
`;

const IccidDisplay = styled.div`
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.gray};
  border-radius: 0.5rem;
  font-size: ${({ theme }) => theme.fontSize.medium};
  word-wrap: break-word;
  hyphens: none;
  color: ${({ theme }) => theme.colors.black};
`;

const ModalButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem; /* More space above buttons */
  padding-top: 1rem;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.colors.gray};
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 0.75rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  text-align: left;
  
  input[type="checkbox"] {
    /* Hide the default checkbox */
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }
  
  label {
    font-size: ${({ theme }) => theme.fontSize.small};
    cursor: pointer;
    line-height: 1.3;
    display: flex;
    align-items: flex-start;
    user-select: none;
    position: relative;
    padding-left: 35px;
  }
  
  /* Create a custom checkbox */
  label::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 24px;
    width: 24px;
    background-color: #fff;
    border: 2px solid ${({ theme }) => theme.colors.gray500};
    border-radius: 4px;
    
    @media (max-width: 850px) {
      height: 28px;
      width: 28px;
    }
  }
  
  /* When the checkbox is checked, add a colored background */
  input[type="checkbox"]:checked + label::before {
    background-color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  /* Create the checkmark/indicator (hidden when not checked) */
  label::after {
    content: '';
    position: absolute;
    left: 9px;
    top: 5px;
    width: 6px;
    height: 12px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    display: none;
    
    @media (max-width: 850px) {
      left: 10px;
      top: 6px;
      width: 8px;
      height: 14px;
    }
  }
  
  /* Show the checkmark when checked */
  input[type="checkbox"]:checked + label::after {
    display: block;
  }
  
  /* Increase the overall container touch area */
  @media (max-width: 850px) {
    padding: 0.75rem 0.5rem;
    margin-top: 0.75rem;
  }
`;

const DigitContainer = styled.div`
  font-family: monospace;
  font-size: 2.5rem;
  font-weight: 700;
  margin-top: 0.75rem;
  letter-spacing: 0.25rem;
  
  @media (max-width: 850px) {
    font-size: 2rem;
  }
`;
